import { useCallback } from "react";

interface UsePaginationProps {
  loadPage: (token?: string) => Promise<void>;
  nextToken: string | null;
  currentPageIndex: number;
  pageTokens: (string | null)[];
  setPageTokens: React.Dispatch<React.SetStateAction<(string | null)[]>>;
  setCurrentPageIndex: React.Dispatch<React.SetStateAction<number>>;
}

interface UsePaginationResult {
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const usePagination = ({
  loadPage,
  nextToken,
  currentPageIndex,
  pageTokens,
  setPageTokens,
  setCurrentPageIndex,
}: UsePaginationProps): UsePaginationResult => {
  const nextPage = useCallback(async () => {
    if (nextToken) {
      const newPageIndex = currentPageIndex + 1;

      setPageTokens((prev) => {
        const updated = [...prev];
        updated[newPageIndex] = nextToken;
        return updated;
      });

      setCurrentPageIndex(newPageIndex);
      await loadPage(nextToken);
    }
  }, [
    nextToken,
    currentPageIndex,
    setPageTokens,
    setCurrentPageIndex,
    loadPage,
  ]);

  const prevPage = useCallback(async () => {
    if (currentPageIndex > 0) {
      const newPageIndex = currentPageIndex - 1;
      const prevToken = pageTokens[newPageIndex] || null;

      setCurrentPageIndex(newPageIndex);
      await loadPage(prevToken ?? undefined);
    }
  }, [currentPageIndex, pageTokens, setCurrentPageIndex, loadPage]);

  return {
    nextPage,
    prevPage,
    hasNextPage: !!nextToken,
    hasPrevPage: currentPageIndex > 0,
  };
};
